import React, { useState } from 'react';
import { Tab, Nav, Accordion, Form } from 'react-bootstrap';
import { FaExclamationTriangle, FaFileAlt } from "react-icons/fa";
import { Button } from "@mui/material";

const RequestButton = () => {
    const [key, setKey] = useState('totalConsent');
    const [panelOpen, setPanelOpen] = useState(false);
    const [activeTotalDuration, setActiveTotalDuration] = useState(null);
    const [activePartialDuration, setActivePartialDuration] = useState(null);

    const handleOpenPanel = () => setPanelOpen(true);
    const handleClosePanel = (e) => e.target === e.currentTarget && setPanelOpen(false);
    const handleGoBack = () => setPanelOpen(false);
    const handleAccept = () => {
        console.log("Consent accepted");
        setPanelOpen(false);
    };

    const [isChecked, setIsChecked] = useState(false);
    const [isCheckedPartial, setIsCheckedPartial] = useState(false);
    const [customTime, setCustomTime] = useState('');

    const handleCheckboxChange = (e) => setIsChecked(e.target.checked);
    const handleCustomTimeChange = (e) => setCustomTime(e.target.value);
    const handleCheckboxChangePartial = (e) => setIsCheckedPartial(e.target.checked);

    const handleTotalDurationClick = (duration) => setActiveTotalDuration(duration);
    const handlePartialDurationClick = (duration) => setActivePartialDuration(duration);

    return (
        <div>
            <button className="btn btn-primary mt-3" onClick={handleOpenPanel}>
                Request New Consents
            </button>

            {panelOpen && (
                <>
                    <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 z-index-998" onClick={handleClosePanel} />

                    <div className="patient-details-panel panel-open position-fixed top-0 end-0 w-50 h-100 bg-white shadow-sm p-4 overflow-auto z-index-999">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <button onClick={handleGoBack} className="btn btn-light border border-secondary px-3 py-2 rounded">
                                Back
                            </button>
                        </div>

                        <Tab.Container activeKey={key} onSelect={(k) => setKey(k)}>
                            <Nav variant="tabs" className="border-bottom border-secondary">
                                <Nav.Item className="w-50">
                                    <Nav.Link
                                        eventKey="totalConsent"
                                        className={`border-0 px-3 py-2 fw-bold text-center ${key === "totalConsent" ? "border-bottom border-3 border-primary" : "text-black"}`}
                                    >
                                        Total Consent
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item className="w-50">
                                    <Nav.Link
                                        eventKey="partialConsent"
                                        className={`border-0 px-3 py-2 fw-bold text-center ${key === "partialConsent" ? "border-bottom border-3 border-primary" : "text-black"}`}
                                    >
                                        Partial Consent
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>

                            <Tab.Content className="p-3">
                                {/* Total Consent Tab */}
                                <Tab.Pane eventKey="totalConsent">
                                    <div className="alert alert-warning d-flex align-items-center mb-4" role="alert">
                                        <FaExclamationTriangle style={{ fontSize: "24px", marginRight: "10px" }} />
                                        <div className="d-flex justify-content-between w-100">
                                            <div>
                                                <h5 className="mb-1 fw-bold">Request Total Consent</h5>
                                                <p className="mb-0" style={{ fontSize: "14px" }}>
                                                    You are requesting full access to Monie Disusa's medical record.
                                                </p>
                                            </div>
                                            <FaFileAlt style={{ fontSize: "24px", color: "#e0a800" }} />
                                        </div>
                                    </div>

                                    <div className="card bg-light mb-4">
                                        <div className="card-body">
                                            <h5 className="card-title">Request Total Consent</h5>
                                            <div className="form-check mb-3">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="requestConsentCheckbox"
                                                    checked={isChecked}
                                                    onChange={handleCheckboxChange}
                                                />
                                                <label className="form-check-label" htmlFor="requestConsentCheckbox">
                                                    I agree to the terms and request the total consent.
                                                </label>
                                            </div>
                                            <p className="card-text">
                                                You are requesting full access to Monie Disusa's medical record for a limited time. The patient will set the duration.
                                            </p>
                                        </div>
                                    </div>

                                    {isChecked && (
                                        <div className="mt-3 d-flex flex-wrap gap-2">
                                            {['1 hour', '6 hours', '12 hours', '24 hours', 'custom'].map((duration) => (
                                                <Button
                                                    key={duration}
                                                    onClick={() => handleTotalDurationClick(duration)}
                                                    sx={{
                                                        backgroundColor: activeTotalDuration === duration ? '#007bff' : 'transparent',
                                                        color: activeTotalDuration === duration ? '#fff' : '#000',
                                                        border: '1px solid transparent',
                                                        textTransform: 'none',
                                                        '&:hover': {
                                                            backgroundColor: activeTotalDuration === duration ? '#0069d9' : '#f0f0f0',
                                                        },
                                                    }}
                                                >
                                                    {duration === 'custom' ? 'Custom' : duration}
                                                </Button>
                                            ))}
                                        </div>
                                    )}

                                    {isChecked && activeTotalDuration === 'custom' && (
                                        <div className="mt-3">
                                            <label htmlFor="customTimeInput" className="form-label">Enter custom duration (in hours):</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                id="customTimeInput"
                                                value={customTime}
                                                onChange={handleCustomTimeChange}
                                                placeholder="Enter time in hours"
                                            />
                                        </div>
                                    )}
                                </Tab.Pane>

                                {/* Partial Consent Tab */}
                                <Tab.Pane eventKey="partialConsent">
                                    <h5 className="mb-1 fw-bold">Request Partial Consent</h5>
                                    <p className="mb-2" style={{ fontSize: "14px" }}>
                                        Choose the medical records you need. The patient will review and approve the request
                                    </p>

                                    <Accordion defaultActiveKey="0" className="mb-4">
                                        <Accordion.Item eventKey="0">
                                            <Accordion.Header>Lab Reports</Accordion.Header>
                                            <Accordion.Body>
                                                <Form.Check type="checkbox" label="CBC (Complete Blood Count)" />
                                                <Form.Check type="checkbox" label="Blood Sugar Test" />
                                                <Form.Check type="checkbox" label="Liver Function Test" />
                                                <Form.Check type="checkbox" label="Kidney Function Test" />
                                            </Accordion.Body>
                                        </Accordion.Item>
                                        <Accordion.Item eventKey="1">
                                            <Accordion.Header>Prescriptions</Accordion.Header>
                                            <Accordion.Body>
                                                <Form.Check type="checkbox" label="Medication List" />
                                                <Form.Check type="checkbox" label="Ongoing Treatment Plans" />
                                            </Accordion.Body>
                                        </Accordion.Item>
                                        <Accordion.Item eventKey="2">
                                            <Accordion.Header>Imaging Scans</Accordion.Header>
                                            <Accordion.Body>
                                                <p>No options available yet.</p>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                        <Accordion.Item eventKey="3">
                                            <Accordion.Header>Surgery Records</Accordion.Header>
                                            <Accordion.Body>
                                                <p>No options available yet.</p>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                        <Accordion.Item eventKey="4">
                                            <Accordion.Header>General Medical History</Accordion.Header>
                                            <Accordion.Body>
                                                <p>No options available yet.</p>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>

                                    <div className="mt-3 d-flex flex-wrap gap-2">
                                        {['1 hour', '6 hours', '12 hours', '24 hours', 'custom'].map((duration) => (
                                            <Button
                                                key={duration}
                                                onClick={() => handlePartialDurationClick(duration)}
                                                sx={{
                                                    backgroundColor: activePartialDuration === duration ? '#007bff' : 'transparent',
                                                    color: activePartialDuration === duration ? '#fff' : '#000',
                                                    border: '1px solid transparent',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: activePartialDuration === duration ? '#0069d9' : '#f0f0f0',
                                                    },
                                                }}
                                            >
                                                {duration === 'custom' ? 'Custom' : duration}
                                            </Button>
                                        ))}
                                    </div>
                                </Tab.Pane>
                            </Tab.Content>
                        </Tab.Container>

                        <button
                            onClick={handleAccept}
                            className="btn btn-primary position-absolute bottom-0 end-0 m-3 px-4 py-2 rounded"
                        >
                            Accept
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default RequestButton;
